import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { parseCsv, rowsToLinkedinUpserts } from '../../lib/linkedinCsvImport';
import { RefreshCw, Search, ChevronUp, ChevronDown, Linkedin, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

export type LinkedinRow = {
  linkedin_slug: string;
  lead_delta_id: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  linkedin_url: string | null;
  email: string | null;
  business_email: string | null;
  personal_email: string | null;
  phone: string | null;
  headline: string | null;
  company_name: string | null;
  location: string | null;
  country: string | null;
  industry: string | null;
  connected_on: string | null;
  followers: number | null;
  languages: string | null;
  date_of_birth: string | null;
  last_messaged: string | null;
  tags: string | null;
  notes: string | null;
  profile_picture_url: string | null;
  extra: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

type SortKey = 'full_name' | 'headline' | 'connected_on' | 'updated_at';

function displayName(row: LinkedinRow): string {
  const f = row.full_name?.trim();
  if (f) return f;
  const fn = row.first_name?.trim() ?? '';
  const ln = row.last_name?.trim() ?? '';
  const joined = `${fn} ${ln}`.trim();
  return joined || '—';
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function extraKeyCount(extra: Record<string, unknown> | null): number {
  if (!extra || typeof extra !== 'object') return 0;
  return Object.keys(extra).length;
}

function leadAvatarSrc(slug: string): string {
  return `https://unavatar.io/linkedin.com/in/${encodeURIComponent(slug)}`;
}

function nameInitialsFromDisplay(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ''}${parts[parts.length - 1]![0] ?? ''}`.toUpperCase() || '?';
}

function primaryEmail(row: LinkedinRow): string | null {
  return row.email?.trim() || row.business_email?.trim() || row.personal_email?.trim() || null;
}

/** Canonical profile URL: stored `linkedin_url`, or built from `linkedin_slug`. */
function linkedinProfileHref(row: LinkedinRow): string | null {
  const raw = row.linkedin_url?.trim();
  if (raw) {
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw.replace(/^\/+/, '')}`;
  }
  const slug = row.linkedin_slug?.trim();
  if (slug) return `https://www.linkedin.com/in/${encodeURIComponent(slug)}/`;
  return null;
}

const LeadAvatar: React.FC<{ slug: string; displayName: string; pictureUrl?: string | null }> = ({
  slug,
  displayName,
  pictureUrl,
}) => {
  const [failed, setFailed] = useState(false);
  const initials = nameInitialsFromDisplay(displayName);
  const fallback = slug.trim() ? leadAvatarSrc(slug) : null;
  const src = (pictureUrl?.trim() || fallback) ?? null;

  if (!src || failed) {
    return (
      <div
        className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-xs font-semibold text-gray-600 dark:text-gray-300 shrink-0"
        aria-hidden
      >
        {initials}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      width={40}
      height={40}
      className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-gray-200 dark:ring-gray-600 bg-gray-100 dark:bg-gray-700"
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
};

const UPSERT_CHUNK = 400;

const LinkedinView: React.FC = () => {
  const [rows, setRows] = useState<LinkedinRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ column: SortKey; direction: 'asc' | 'desc' } | null>(
    { column: 'connected_on', direction: 'desc' }
  );
  const [detail, setDetail] = useState<LinkedinRow | null>(null);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('linkedin').select('*').limit(5000);

      if (error) throw error;
      setRows((data ?? []) as LinkedinRow[]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      toast.error(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const handleCsvSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Choose a .csv file');
      return;
    }
    setImporting(true);
    try {
      const text = await file.text();
      const { headers, rows: dataRows } = parseCsv(text);
      if (headers.length === 0) {
        toast.error('No header row found');
        return;
      }
      const { records, stats } = rowsToLinkedinUpserts(headers, dataRows);
      if (records.length === 0) {
        toast.error(
          'No rows could be imported. Each row needs a LinkedIn profile URL (e.g. column "LinkedIn") so we can extract the /in/ slug as the primary key.'
        );
        return;
      }
      for (let i = 0; i < records.length; i += UPSERT_CHUNK) {
        const chunk = records.slice(i, i + UPSERT_CHUNK);
        const { error } = await supabase.from('linkedin').upsert(chunk, { onConflict: 'linkedin_slug' });
        if (error) {
          console.error('linkedin upsert chunk', i, error);
          const det = 'details' in error && error.details ? String(error.details) : '';
          const hint = 'hint' in error && error.hint ? String(error.hint) : '';
          const code = 'code' in error && error.code ? String(error.code) : '';
          const msg = [error.message, code && `(${code})`, det, hint].filter(Boolean).join(' — ');
          throw new Error(msg || 'Upsert failed');
        }
      }
      const parts = [
        `Upserted ${records.length} row(s) (same profile slug updates existing row)`,
        stats.csvDuplicateSlugs > 0
          ? `${stats.csvDuplicateSlugs} CSV row(s) merged (duplicate LinkedIn profile in file; last row kept)`
          : null,
        stats.skippedNoProfile > 0 ? `${stats.skippedNoProfile} skipped (no usable LinkedIn URL)` : null,
      ].filter(Boolean);
      toast.success(parts.join('. ') + '.');
      void fetchRows();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const filteredSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (!q) return true;
      const blob = [
        r.linkedin_slug,
        r.lead_delta_id,
        r.full_name,
        r.first_name,
        r.last_name,
        r.headline,
        r.company_name,
        r.email,
        r.business_email,
        r.personal_email,
        r.location,
        r.tags,
        r.notes,
        r.profile_picture_url,
      ]
        .map((x) => (x ?? '').toLowerCase())
        .join(' ');
      return blob.includes(q);
    });

    if (sortConfig) {
      const { column, direction } = sortConfig;
      const mul = direction === 'asc' ? 1 : -1;
      list = [...list].sort((a, b) => {
        if (column === 'connected_on' || column === 'updated_at') {
          const ta = a[column] ? new Date(a[column] as string).getTime() : 0;
          const tb = b[column] ? new Date(b[column] as string).getTime() : 0;
          return (ta - tb) * mul;
        }
        const sa = String(a[column] ?? '').toLowerCase();
        const sb = String(b[column] ?? '').toLowerCase();
        if (sa < sb) return -1 * mul;
        if (sa > sb) return 1 * mul;
        return 0;
      });
    }

    return list;
  }, [rows, searchQuery, sortConfig]);

  const toggleSort = (column: SortKey) => {
    setSortConfig((prev) => {
      if (!prev || prev.column !== column) {
        return { column, direction: column === 'connected_on' || column === 'updated_at' ? 'desc' : 'asc' };
      }
      return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const SortIcon: React.FC<{ column: SortKey }> = ({ column }) => {
    if (!sortConfig || sortConfig.column !== column) {
      return <ChevronUp className="w-3.5 h-3.5 opacity-30 inline ml-0.5" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-3.5 h-3.5 inline ml-0.5" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 inline ml-0.5" />
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-500 dark:text-gray-400">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
        <p className="text-sm">Loading LinkedIn export…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Linkedin className="w-7 h-7 text-cyan-600 dark:text-cyan-400" />
            LinkedIn (Lead Delta)
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
            Review rows in the <code className="text-xs">linkedin</code> table.{' '}
            <strong className="font-medium text-gray-700 dark:text-gray-300">Import CSV</strong> upserts on the
            LinkedIn <code className="text-xs">/in/</code> profile slug (parsed from your LinkedIn URL column).
            Re-imports merge on the same profile. Showing up to 5,000 rows.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(ev) => void handleCsvSelected(ev)}
          />
          <button
            type="button"
            disabled={importing}
            onClick={() => csvInputRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm font-medium hover:bg-cyan-700 disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {importing ? 'Importing…' : 'Import CSV'}
          </button>
          <button
            type="button"
            onClick={() => void fetchRows()}
            disabled={importing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search name, company, headline, email, tags…"
          className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        />
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">
                  <button type="button" onClick={() => toggleSort('full_name')} className="inline-flex items-center">
                    Contact
                    <SortIcon column="full_name" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">
                  <button type="button" onClick={() => toggleSort('headline')} className="inline-flex items-center">
                    Role &amp; company
                    <SortIcon column="headline" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">
                  <button type="button" onClick={() => toggleSort('connected_on')} className="inline-flex items-center">
                    Connected
                    <SortIcon column="connected_on" />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-semibold text-gray-700 dark:text-gray-200">Extra</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredSorted.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                    {rows.length === 0
                      ? 'No rows yet. Import a Lead Delta CSV with a LinkedIn profile URL column.'
                      : 'No matches for this search.'}
                  </td>
                </tr>
              ) : (
                filteredSorted.map((r) => {
                  const nm = displayName(r);
                  const em = primaryEmail(r);
                  const liHref = linkedinProfileHref(r);
                  return (
                    <tr
                      key={r.linkedin_slug}
                      className="hover:bg-gray-50 dark:hover:bg-gray-900/40 cursor-pointer"
                      onClick={() => setDetail(r)}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <LeadAvatar slug={r.linkedin_slug} displayName={nm} pictureUrl={r.profile_picture_url} />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{nm}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 break-all">
                              {em ?? '—'}
                            </div>
                          </div>
                          {liHref ? (
                            <a
                              href={liHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shrink-0 rounded-lg p-1.5 text-[#0A66C2] hover:bg-cyan-50 dark:text-[#70b6ff] dark:hover:bg-gray-700 transition-colors"
                              title="Open LinkedIn profile"
                              aria-label="Open LinkedIn profile"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Linkedin className="h-5 w-5" strokeWidth={1.75} />
                            </a>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 max-w-xs sm:max-w-md">
                        <div className="text-sm text-gray-800 dark:text-gray-200 line-clamp-2">
                          {r.headline?.trim() ? r.headline.trim() : '—'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                          {r.company_name?.trim() ? r.company_name.trim() : '—'}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatWhen(r.connected_on)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs text-gray-500">
                        {extraKeyCount(r.extra) > 0 ? `${extraKeyCount(r.extra)} fields` : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30 text-xs text-gray-500 dark:text-gray-400">
          {filteredSorted.length} row{filteredSorted.length === 1 ? '' : 's'} shown
          {rows.length >= 5000 ? ' (import limit query: increase in code if needed)' : ''}
        </div>
      </div>

      {detail ? (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 dark:bg-black/60"
            aria-label="Close"
            onClick={() => setDetail(null)}
          />
          <div
            className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{displayName(detail)}</h2>
                <p className="text-xs font-mono text-gray-500 dark:text-gray-400 mt-1">
                  /in/{detail.linkedin_slug}
                  {detail.lead_delta_id ? ` · Lead Delta ID: ${detail.lead_delta_id}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-4 text-sm">
              {detail.linkedin_url ? (
                <a
                  href={detail.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-cyan-600 dark:text-cyan-400 hover:underline break-all"
                >
                  {detail.linkedin_url}
                </a>
              ) : null}
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {(
                  [
                    ['Headline', detail.headline],
                    ['Company', detail.company_name],
                    ['Location', detail.location],
                    ['Country', detail.country],
                    ['Industry', detail.industry],
                    ['Email', detail.email],
                    ['Business email', detail.business_email],
                    ['Personal email', detail.personal_email],
                    ['Phone', detail.phone],
                    ['Connected', formatWhen(detail.connected_on)],
                    ['Followers', detail.followers != null ? String(detail.followers) : null],
                    ['Languages', detail.languages],
                    ['Date of birth', detail.date_of_birth],
                    ['Last messaged', formatWhen(detail.last_messaged)],
                    ['Tags', detail.tags],
                    ['Profile picture', detail.profile_picture_url],
                  ] as const
                ).map(([label, val]) =>
                  val ? (
                    <div key={label} className="sm:col-span-2">
                      <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {label}
                      </dt>
                      <dd className="mt-0.5 text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-all">
                        {label === 'Profile picture' && /^https?:\/\//i.test(String(val)) ? (
                          <a
                            href={String(val)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-600 dark:text-cyan-400 hover:underline"
                          >
                            {val}
                          </a>
                        ) : (
                          val
                        )}
                      </dd>
                    </div>
                  ) : null
                )}
              </dl>
              {detail.notes?.trim() ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Notes
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-gray-900 dark:text-gray-100">{detail.notes.trim()}</p>
                </div>
              ) : null}
              {extraKeyCount(detail.extra) > 0 ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Extra (JSON)
                  </p>
                  <pre className="text-xs p-3 rounded-lg bg-gray-100 dark:bg-gray-900 overflow-x-auto text-gray-800 dark:text-gray-200 max-h-48">
                    {JSON.stringify(detail.extra, null, 2)}
                  </pre>
                </div>
              ) : null}
              <p className="text-xs text-gray-400 dark:text-gray-500">
                updated_at {formatWhen(detail.updated_at)} · created_at {formatWhen(detail.created_at)}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LinkedinView;
